import { ChevronDownIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { BULK_ACTIVATION_KINDS, getBulkActivationLabel } from '@/lib/bulkActivation'
import type { BulkActivationKind } from '@/lib/bulkActivation'

const ACTIVATING_LABEL = 'Aktiverar...'

function formatCustomerCount(count: number): string {
  const noun = count === 1 ? 'kund' : 'kunder'

  return `${count} ${noun}`
}

interface CustomerSelectionToolbarProps {
  selectedCount: number
  totalCount: number
  eligibleCounts: Record<BulkActivationKind, number>
  canManageSubscriptions: boolean
  isActivating: boolean
  onClearSelection: () => void
  onActivate: (kind: BulkActivationKind) => void
}

export function CustomerSelectionToolbar({
  selectedCount,
  totalCount,
  eligibleCounts,
  canManageSubscriptions,
  isActivating,
  onClearSelection,
  onActivate
}: CustomerSelectionToolbarProps): React.ReactElement {
  const hasSelection = selectedCount > 0
  // Antalet gäller hela träfflistan, inte sidan – därför kommer det från backend och inte från raderna.
  const summary = hasSelection ? `${selectedCount} valda` : formatCustomerCount(totalCount)
  const canOpenMenu = hasSelection && !isActivating
  const triggerLabel = isActivating ? ACTIVATING_LABEL : `Aktivera (${selectedCount})`

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-6 py-4">
      <p className="text-sm text-muted-foreground">{summary}</p>
      <div className="flex items-center gap-2">
        {hasSelection && (
          <Button
            type="button"
            variant="ghost"
            className="rounded-none"
            onClick={onClearSelection}
          >
            Avmarkera
          </Button>
        )}
        {canManageSubscriptions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="rounded-none"
                disabled={!canOpenMenu}
              >
                {triggerLabel}
                <ChevronDownIcon className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-56"
            >
              {BULK_ACTIVATION_KINDS.map((kind) => (
                <DropdownMenuItem
                  key={kind}
                  disabled={eligibleCounts[kind] === 0}
                  onSelect={() => onActivate(kind)}
                >
                  {getBulkActivationLabel(kind, eligibleCounts[kind])}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
